import { useCallback, useMemo } from "react";
import { coordEach } from "@turf/turf";
import { around } from "geokdbush";
import KDBush from "kdbush";
import type { FeatureCollection, GeoJsonProperties, Point } from "geojson";

/** provides a function that looks up the closest features in a collection */
export function useClosest<T extends GeoJsonProperties>(
  collection?: FeatureCollection<Point, T>
) {
  // create an index
  const index = useMemo(() => {
    if (!collection) return null;

    const index = new KDBush(collection.features.length);

    coordEach(collection, ([lon, lat]) => {
      index.add(lon, lat);
    });

    index.finish();

    return index;
  }, [collection]);

  const lookup = useCallback(
    (lon: number, lat: number, count = 1) => {
      if (!(index && collection)) return [];

      const indexes = around(index, lon, lat, count) as number[];

      return indexes.map((i) => collection.features[i]);
    },
    [index, collection]
  );

  return lookup;
}
